import { Provider } from 'react-redux';
import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import ResourceHandler from 'applications/common/resourcehandler/index.js';
import routeResolverFactory from 'applications/common/stores/router/route-resolver-factory.js';
import subscriptionResolverFactory from './subscription-resolver/subscription-resolver-factory.js';
import ReactDomClient from 'react-dom/client';

const { dispatch } = MojitoCore.Services.redux.store;
const intentActions = MojitoCore.Intents.actions;
const IntentTypes = MojitoPresentation.Base.Intent.Types;
const reduxInstance = MojitoCore.Services.redux;
const { waitFor } = MojitoCore.Utils;

/**
 * @typedef AppConfig
 * @type {object}
 * @property {*} core - The configuration for the core layer of the application.
 * @property {Mojito.Services.Config} data - The configuration for the services layer of the application.
 * @property {*} resources - The configuration for the resources layer of the application.
 *
 * @memberof Mojito.Applications.Sports
 */

class Main {
    constructor() {
        this.initialized = false;
        this.rootElementId = null;
        this.rootElement = null;
        this.appConfig = {};
    }

    /**
     * Initialize the application layer and subsequent layers given the configuration.
     * Typically used in conjunction with {@link Mojito.Applications.Sports#render|render} and must be called before it.
     *
     * @param {Mojito.Applications.Sports.AppConfig} appConfig - The Application configuration.
     * @function Mojito.Applications.Sports#init
     */
    async init(appConfig) {
        this.appConfig = this.ensureDestinationPrefix(appConfig);
        await waitFor();
        await this.initLayers(this.appConfig);
        routeResolverFactory.setRouteResolver(
            this.appConfig.application?.stores?.router?.routeResolver
        );
        await waitFor();
        const { actions: applicationActions } = require('./stores/application/slice.js');
        subscriptionResolverFactory.setSubscriptionResolver(
            this.appConfig.application?.subscriptionResolver
        );
        dispatch(applicationActions.configure(this.appConfig));
        this.initialized = true;
    }

    /**
     * Renders Mojito application on root element.
     * _NOTE:_ Must be called after {@link Mojito.Applications.Sports#init|init} resolved, else an error is thrown.
     *
     * @param {string} rootElementId - Id of the Dom element to use as the root for the Mojito Application.
     * @function Mojito.Applications.Sports#render
     */
    async render(rootElementId) {
        if (!this.initialized) {
            throw new Error(
                'Application is not initialized. Make sure Mojito.Applications.Sports.init is done before calling render.'
            );
        }
        this.rootElementId = rootElementId;
        this.rootElement = ReactDomClient.createRoot(document.getElementById(this.rootElementId));
        await waitFor();
        const MainSportsbook = require('./main/index.js').default;
        const mainView = (
            <Provider store={reduxInstance.store}>
                <MainSportsbook />
            </Provider>
        );
        this.rootElement.render(mainView);
    }

    /**
     * Terminates Mojito application. Used to stop background processing.
     * Typically used in conjunction with {@link Mojito.Applications.Sports#unmount|unmount}.
     *
     * @function Mojito.Applications.Sports#terminate
     */
    terminate() {
        const routerActions = require('applications/common/stores/router/slice.js').actions;
        dispatch(routerActions.reset());
        const coreBootstrap = require('mojito/core/bootstrap.js').default;
        coreBootstrap.dispose();
        const servicesBootstrap = require('mojito/services/bootstrap-sports.js').default;
        servicesBootstrap.dispose();
        const presentationBootstrap = require('mojito/presentation/bootstrap.js').default;
        presentationBootstrap.dispose();
    }

    /**
     * Unmounts Mojito application. Used to properly remove mojito from DOM.
     * Typically used in conjunction with {@link Mojito.Applications.Sports#terminate|terminate}.
     *
     * @function Mojito.Applications.Sports#unmount
     */
    unmount() {
        if (!this.rootElement) {
            return;
        }
        this.rootElement.unmount();
    }

    async initLayers(config) {
        const coreBootstrap = require('mojito/core/bootstrap.js').default;
        coreBootstrap.init(config.core);
        await waitFor();
        const servicesBootstrap = require('mojito/services/bootstrap-sports.js').default;
        await servicesBootstrap.init(config.services);
        await waitFor();
        const modulesBootstrap = require('mojito/modules/bootstrap.js').default;
        modulesBootstrap.init(config.modules);
        ResourceHandler.init(config.resources);
        await waitFor();
        const presentationBootstrap = require('mojito/presentation/bootstrap.js').default;
        presentationBootstrap.init();
        dispatch(intentActions.publishIntent(IntentTypes.MOJITO_INITIALIZED));
    }

    ensureDestinationPrefix(config) {
        const { destinationPrefix: endpointPrefix } = config.core || {};
        const destinationPrefix = endpointPrefix || '/api';
        return { ...config, core: { ...(config.core || {}), destinationPrefix } };
    }
}

export default new Main();
